<template>
	<SideNavLayout overlay-nav>
		<div class="settings-page">
			<Transition name="settings-fade" mode="out-in">
				<div
					v-if="isInitialProfileLoading && store.isUserLoggedIn"
					key="loading"
					class="settings-shell"
					aria-busy="true"
				>
					<span class="skeleton-block skeleton-title" aria-hidden="true"></span>
				</div>

				<div v-else-if="!store.isUserLoggedIn" key="guest-content" class="settings-shell">
					<section class="settings-card" aria-labelledby="general-preferences-title">
						<h2 id="general-preferences-title" class="label">
							{{ $t("settingsPage.sections.general.title") }}
						</h2>

						<div class="row">
							<div class="copy">
								<h3>{{ $t("my-profile.language.title") }}</h3>
								<p>{{ $t("my-profile.language.description") }}</p>
							</div>
							<div class="pill-toggle" role="radiogroup" :aria-label="$t('my-profile.language.title')">
								<label
									v-for="option in languageOptions"
									:key="option.id"
									class="option"
									:class="{ selected: languageDraft === option.id }"
								>
									<input
										type="radio"
										name="preferred-language"
										:value="option.id"
										:checked="languageDraft === option.id"
										@change="handleGuestLanguageChange(option.id)"
									/>
									<span>{{ option.label }}</span>
								</label>
							</div>
						</div>
					</section>

					<section class="settings-card" aria-labelledby="privacy-settings-title">
						<h2 id="privacy-settings-title" class="label">{{ $t("privacy.settings") }}</h2>
						<div class="row">
							<div class="copy">
								<h3>{{ $t("privacy.title") }}</h3>
								<p>{{ $t("settingsPage.privacy.description") }}</p>
							</div>
							<Button variant="secondary" size="s" @click="privacy.openSettings()">
								{{ $t("privacy.changeSettings") }}
							</Button>
						</div>
					</section>
				</div>

				<div v-else key="content" class="settings-shell">
					<p v-if="preferencesError" class="error-message">{{ preferencesError }}</p>

					<section class="settings-card" aria-labelledby="personal-info-title">
						<h2 id="personal-info-title" class="label">
							{{ $t("settingsPage.sections.personalInfo.title") }}
						</h2>

						<div class="identity-row">
							<div class="avatar" aria-hidden="true">{{ initials }}</div>
							<div class="identity">
								<h3>{{ fullName }}</h3>
								<p class="email">{{ email }}</p>
							</div>
						</div>

						<div class="field-grid">
							<label class="field">
								<h3>{{ $t("settingsPage.personalInfo.firstName") }}</h3>
								<input v-model="firstNameDraft" type="text" :disabled="isSavingPreferences" />
							</label>
							<label class="field">
								<h3>{{ $t("settingsPage.personalInfo.lastName") }}</h3>
								<input v-model="lastNameDraft" type="text" :disabled="isSavingPreferences" />
							</label>
						</div>
					</section>

					<section class="settings-card" aria-labelledby="general-preferences-title">
						<h2 id="general-preferences-title" class="label">
							{{ $t("settingsPage.sections.general.title") }}
						</h2>

						<div class="row">
							<div class="copy">
								<h3>{{ $t("my-profile.language.title") }}</h3>
								<p>{{ $t("my-profile.language.description") }}</p>
							</div>
							<div class="pill-toggle" role="radiogroup" :aria-label="$t('my-profile.language.title')">
								<label
									v-for="option in languageOptions"
									:key="option.id"
									class="option"
									:class="{ selected: languageDraft === option.id }"
								>
									<input
										type="radio"
										name="preferred-language"
										:value="option.id"
										:checked="languageDraft === option.id"
										:disabled="isLoadingPreferences || isSavingPreferences"
										@change="handleLanguagePreferenceChange(option.id)"
									/>
									<span>{{ option.label }}</span>
								</label>
							</div>
						</div>

						<div class="row">
							<div class="copy">
								<h3>{{ $t("settingsPage.areaUnit.label") }}</h3>
								<p>{{ $t("settingsPage.areaUnit.description") }}</p>
							</div>
							<div class="pill-toggle" role="radiogroup" :aria-label="$t('settingsPage.areaUnit.label')">
								<label
									v-for="option in unitOptions"
									:key="option.id"
									class="option"
									:class="{ selected: unitDraft === option.id }"
								>
									<input
										type="radio"
										name="unit"
										:value="option.id"
										:checked="unitDraft === option.id"
										:disabled="isLoadingPreferences || isSavingPreferences"
										@change="handleUnitPreferenceChange(option.id)"
									/>
									<span>{{ option.label }}</span>
								</label>
							</div>
						</div>

						<div class="row">
							<div class="copy">
								<h3>{{ $t("settingsPage.defaultPage.label") }}</h3>
								<p>{{ $t("settingsPage.defaultPage.description") }}</p>
							</div>
							<div class="default-page-options" role="radiogroup" :aria-label="$t('settingsPage.defaultPage.label')">
								<Button
									v-for="option in defaultPageOptions"
									:key="option"
									:variant="defaultPageDraft === option ? 'primary' : 'tertiary'"
									size="s"
									shape="pill"
									role="radio"
									:aria-checked="defaultPageDraft === option"
									:disabled="isLoadingPreferences || isSavingPreferences"
									@click="handleDefaultPagePreferenceChange(option)"
								>
									{{ $t(`settingsPage.defaultPage.options.${option}`) }}
								</Button>
							</div>
						</div>
					</section>

					<section class="settings-card" aria-labelledby="delivery-channels-title">
						<h2 id="delivery-channels-title" class="label">
							{{ $t("settingsPage.sections.delivery.title") }}
						</h2>

						<div class="row expandable">
							<div class="row-header">
								<div class="copy">
									<h3>{{ $t("my-profile.notifications.in-app-options.savedSearches.label") }}</h3>
									<p>{{ $t("my-profile.notifications.in-app-options.savedSearches.description") }}</p>
								</div>
								<ArkBaseSwitch
									:checked="isSearchesEnabled"
									:disabled="isLoadingPreferences || isSavingPreferences"
									@change="handleSavedSearchesChange"
								/>
							</div>

							<div v-if="isSearchesEnabled" class="channel-checkboxes">
								<Button
									v-for="preference in preferences"
									:key="preference.channel"
									:variant="isPreferenceChecked(preference) ? 'primary' : 'tertiary'"
									size="s"
									shape="pill"
									:disabled="isPreferenceDisabled(preference)"
									@click="handleNotificationChannelChange(preference)"
								>
									{{ getPreferenceLabel(preference.channel) }}
								</Button>
							</div>
						</div>

						<div class="row">
							<div class="field">
								<h3>
									<label for="phone-number">{{ $t("my-profile.phone.title") }}</label>
								</h3>
								<input
									id="phone-number"
									v-model="phoneNumberDraft"
									type="tel"
									autocomplete="tel"
									:placeholder="$t('my-profile.phone.placeholder')"
									:disabled="isSavingPreferences"
								/>

								<p v-if="isPhoneVerificationOffered" class="note">{{ $t("my-profile.phone.note.unverified") }}</p>
								<p v-else-if="isSmsPreferenceBlocked" class="warning-message">
									{{ $t("my-profile.notifications.sms-requires-verified-phone") }}
								</p>

								<p v-if="phoneVerificationError && !isPhonePopoverOpen" class="note error">
									{{ phoneVerificationError }}
								</p>
								<p v-else-if="isPhoneVerified && !phoneNumberDraftHasChanged" class="note">{{ phoneNote }}</p>
							</div>

							<Popover.Root
								v-if="isPhoneVerificationOffered"
								v-model:open="isPhonePopoverOpen"
								:positioning="{ placement: 'bottom-end', gutter: 4 }"
							>
								<Popover.Trigger as-child>
									<Button variant="primary" size="s" shape="pill" @click="openPhoneVerification">{{
										$t("my-profile.phone.actions.verify")
									}}</Button>
								</Popover.Trigger>
								<Popover.Positioner>
									<Popover.Content class="phone-popover">
										<label for="phone-verification-code">{{ $t("my-profile.phone.code-label") }}</label>
										<input
											id="phone-verification-code"
											v-model="phoneVerificationCode"
											type="text"
											inputmode="numeric"
											autocomplete="one-time-code"
											:placeholder="$t('my-profile.phone.code-placeholder')"
											@keyup.enter="confirmAndClosePhonePopover"
										/>

										<!-- Always rendered so the buttons below do not shift when a message appears. -->
										<p class="feedback" :class="phoneVerificationError ? 'error' : 'success'">
											{{ phoneVerificationError ?? phoneVerificationMessage }}
										</p>

										<div class="buttons">
											<Button
												variant="tertiary"
												size="s"
												:disabled="!canStartPhoneVerification"
												@click="startPhoneVerificationFlow"
											>
												{{ startPhoneVerificationButtonLabel }}
											</Button>
											<Button
												variant="primary"
												size="s"
												:disabled="!canConfirmPhoneVerification"
												@click="confirmAndClosePhonePopover"
											>
												{{ confirmPhoneVerificationButtonLabel }}
											</Button>
										</div>
									</Popover.Content>
								</Popover.Positioner>
							</Popover.Root>
						</div>

						<div class="row placeholder">
							<div class="copy">
								<h3>
									{{ $t("my-profile.notifications.in-app-options.projects.label") }}
									<span class="status-pill">{{ $t("my-profile.notifications.in-app-options.projects.tag") }}</span>
								</h3>
								<p>{{ $t("my-profile.notifications.in-app-options.projects.description") }}</p>
							</div>
							<ArkBaseSwitch :checked="true" :disabled="true" />
						</div>
					</section>

					<section class="settings-card" aria-labelledby="privacy-settings-title">
						<h2 id="privacy-settings-title" class="label">{{ $t("privacy.settings") }}</h2>
						<div class="row">
							<div class="copy">
								<h3>{{ $t("privacy.title") }}</h3>
								<p>{{ $t("settingsPage.privacy.description") }}</p>
							</div>
							<Button variant="secondary" size="s" @click="privacy.openSettings()">
								{{ $t("privacy.changeSettings") }}
							</Button>
						</div>
					</section>

					<Transition name="save-bar-fade">
						<footer v-if="hasPersonalInfoPendingChanges" class="save-bar">
							<div class="save-bar-inner">
								<Button
									variant="tertiary"
									size="m"
									:disabled="isLoadingPreferences || isSavingPreferences"
									@click="resetPersonalInfoChanges"
								>
									{{ $t("my-profile.actions.reset") }}
								</Button>
								<Button
									variant="primary"
									size="m"
									:disabled="isLoadingPreferences || isSavingPreferences"
									@click="savePersonalInfoChanges"
								>
									{{ saveButtonLabel }}
								</Button>
							</div>
						</footer>
					</Transition>
				</div>
			</Transition>
		</div>
	</SideNavLayout>

	<Teleport to="body">
		<div v-if="showLeaveConfirm" class="modal-overlay" @click="cancelLeave">
			<div class="modal-content" @click.stop>
				<button
					type="button"
					class="icon-button"
					:aria-label="$t('settingsPage.leaveConfirm.cancel')"
					@click="cancelLeave"
				>
					<AppIcon :name="IconName.Close" class="close-icon" />
				</button>
				<h3>{{ $t("settingsPage.leaveConfirm.title") }}</h3>
				<p>{{ $t("settingsPage.leaveConfirm.description") }}</p>
				<div class="modal-actions">
					<Button variant="secondary" size="m" @click="confirmLeaveWithoutSaving">
						{{ $t("settingsPage.leaveConfirm.discard") }}
					</Button>
					<Button variant="primary" size="m" :disabled="isSavingPreferences" @click="confirmLeaveAndSave">
						{{ $t("settingsPage.leaveConfirm.save") }}
					</Button>
				</div>
			</div>
		</div>
	</Teleport>
</template>

<script setup lang="ts">
import { usePrivacyStore } from "@/stores/privacyStore"
import { computed, onMounted, onUnmounted, ref, watch } from "vue"
import { Popover } from "@ark-ui/vue"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import { useI18n } from "vue-i18n"
import { onBeforeRouteLeave } from "vue-router"

import Button from "@/components/ui/Button.vue"
import ArkBaseSwitch from "@/components/ui/ArkBaseSwitch.vue"
import SideNavLayout from "@/components/layout/SideNavLayout.vue"
import { UseRootStore } from "@/model/RootStore"
import { usePhoneVerification } from "@/composables/usePhoneVerification"
import { useNotificationPreferences, type NotificationChannel } from "@/composables/useNotificationPreferences"
import { useLanguagePreference } from "@/composables/useLanguagePreference"
import { getUserInitials } from "@/utils/user"
import { DefaultPages, useDefaultPagePreference } from "@/composables/useDefaultPagePreference"
import { useUnitPreference } from "@/composables/useUnitPreference"

const privacy = usePrivacyStore()

const store = UseRootStore()
const { t } = useI18n()

const wasSaved = ref(false)
const isSavingPreferences = ref(false)
const isInitialProfileLoading = ref(true)

const defaultPageOptions = DefaultPages

const user = computed(() => store.currentUser)
const userInfo = computed(() => user.value.getInfo())
const email = computed(() => userInfo.value?.email ?? "")
const fullName = computed(() => user.value.getFullName())
const initials = computed(() => getUserInitials(fullName.value, "LA"))
const firstNameDraft = ref(userInfo.value?.firstName ?? "")
const lastNameDraft = ref(userInfo.value?.lastName ?? "")
const savedFirstName = ref(firstNameDraft.value)
const savedLastName = ref(lastNameDraft.value)

const {
	phoneNumberDraft,
	phoneNumberDraftHasChanged,
	phoneVerificationCode,
	phoneVerificationMessage,
	phoneVerificationError,
	hasSavedPhoneNumber,
	isPhoneVerified,
	isSmsPreferenceBlocked,
	isPhoneVerificationPending,
	isPhoneVerificationOffered,
	canStartPhoneVerification,
	canConfirmPhoneVerification,
	phoneNote,
	startPhoneVerificationButtonLabel,
	confirmPhoneVerificationButtonLabel,
	cancelPhoneNumberChange,
	savePhoneNumberFlow,
	startPhoneVerificationFlow,
	requestPhoneVerificationCode,
	confirmPhoneVerificationFlow,
} = usePhoneVerification()

const isPhonePopoverOpen = ref(false)

watch(isPhoneVerificationOffered, offered => {
	if (!offered) isPhonePopoverOpen.value = false
})

const openPhoneVerification = async () => {
	await requestPhoneVerificationCode()
	// The number could not be saved, so there is no code to enter; the error shows under the field.
	if (phoneNumberDraftHasChanged.value) isPhonePopoverOpen.value = false
}

const confirmAndClosePhonePopover = async () => {
	await confirmPhoneVerificationFlow()
	if (isPhoneVerified.value) isPhonePopoverOpen.value = false
}

const hasNamePendingChanges = computed(
	() => firstNameDraft.value !== savedFirstName.value || lastNameDraft.value !== savedLastName.value
)

const hasPersonalInfoPendingChanges = computed(() => hasNamePendingChanges.value || phoneNumberDraftHasChanged.value)

const syncPersonalInfoFromProfile = () => {
	firstNameDraft.value = userInfo.value?.firstName ?? ""
	lastNameDraft.value = userInfo.value?.lastName ?? ""
	savedFirstName.value = firstNameDraft.value
	savedLastName.value = lastNameDraft.value
}

const savePersonalInfo = async () => {
	if (!hasPersonalInfoPendingChanges.value) {
		return
	}

	if (hasNamePendingChanges.value) {
		await store.savePersonalInfo(firstNameDraft.value, lastNameDraft.value)
		syncPersonalInfoFromProfile()
	}

	if (phoneNumberDraftHasChanged.value) {
		await savePhoneNumberFlow()
		if (phoneVerificationError.value) throw new Error(phoneVerificationError.value)
	}
}

const resetPersonalInfo = () => {
	firstNameDraft.value = savedFirstName.value
	lastNameDraft.value = savedLastName.value
	cancelPhoneNumberChange()
}

const {
	preferences,
	isLoadingPreferences,
	preferencesError,
	isPreferenceDisabled,
	isPreferenceChecked,
	loadPreferences,
	setPreferenceEnabled: setPreferenceEnabledBase,
	saveNotificationPreferences,
} = useNotificationPreferences(isSmsPreferenceBlocked, isSavingPreferences)

const { languageDraft, languageOptions, syncLanguageFromProfile, saveLanguagePreference } = useLanguagePreference()

const { defaultPageDraft, syncDefaultPageFromProfile, saveDefaultPagePreference } = useDefaultPagePreference()

const { unitDraft, unitOptions, syncUnitFromProfile, saveUnitPreference } = useUnitPreference()

const saveButtonLabel = computed(() =>
	isSavingPreferences.value
		? t("my-profile.status.saving")
		: wasSaved.value && !hasPersonalInfoPendingChanges.value
			? t("my-profile.status.saved")
			: t("my-profile.actions.save")
)

const getPreferenceLabel = (channel: NotificationChannel) => t(`my-profile.notifications.channels.${channel}.label`)

const setPreferenceEnabled = (preference: Parameters<typeof setPreferenceEnabledBase>[0], enabled: boolean) => {
	setPreferenceEnabledBase(preference, enabled)
	wasSaved.value = false
}

const saveSettingChanges = async (save: () => Promise<void>) => {
	if (!store.isUserLoggedIn || isSavingPreferences.value) {
		return false
	}

	isSavingPreferences.value = true
	preferencesError.value = null

	try {
		await save()

		return true
	} catch (error) {
		preferencesError.value = error instanceof Error ? error.message : String(error)

		return false
	} finally {
		isSavingPreferences.value = false
	}
}

const handleLanguagePreferenceChange = async (language: typeof languageDraft.value) => {
	languageDraft.value = language
	await saveSettingChanges(saveLanguagePreference)
}

const handleDefaultPagePreferenceChange = async (defaultPage: (typeof DefaultPages)[number]) => {
	defaultPageDraft.value = defaultPage
	await saveSettingChanges(saveDefaultPagePreference)
}

const handleUnitPreferenceChange = async (unit: typeof unitDraft.value) => {
	unitDraft.value = unit
	await saveSettingChanges(saveUnitPreference)
}

const handleNotificationChannelChange = async (preference: Parameters<typeof setPreferenceEnabledBase>[0]) => {
	setPreferenceEnabled(preference, !isPreferenceChecked(preference))
	await saveSettingChanges(saveNotificationPreferences)
}

const isSearchesEnabled = computed(() => preferences.value.some(preference => isPreferenceChecked(preference)))

const handleSavedSearchesChange = async (enabled: boolean) => {
	if (enabled) {
		const inAppPreference = preferences.value.find(preference => preference.channel === "in_app")
		if (inAppPreference) setPreferenceEnabled(inAppPreference, true)
	} else {
		for (const preference of preferences.value) setPreferenceEnabled(preference, false)
	}

	await saveSettingChanges(saveNotificationPreferences)
}

const resetPersonalInfoChanges = () => {
	resetPersonalInfo()
	wasSaved.value = false
	preferencesError.value = null
}

const SAVED_BADGE_DURATION_MS = 3000
let savedBadgeTimeout: ReturnType<typeof setTimeout> | null = null

const savePersonalInfoChanges = async () => {
	const saved = await saveSettingChanges(savePersonalInfo)

	if (!saved) {
		return
	}

	wasSaved.value = true

	if (savedBadgeTimeout) {
		clearTimeout(savedBadgeTimeout)
	}

	savedBadgeTimeout = setTimeout(() => {
		wasSaved.value = false
	}, SAVED_BADGE_DURATION_MS)
}

const showLeaveConfirm = ref(false)
let resolveLeaveConfirm: ((leave: boolean) => void) | null = null

onBeforeRouteLeave(() => {
	if (!hasPersonalInfoPendingChanges.value) {
		return true
	}

	return new Promise<boolean>(resolve => {
		resolveLeaveConfirm = resolve
		showLeaveConfirm.value = true
	})
})

function cancelLeave() {
	showLeaveConfirm.value = false
	resolveLeaveConfirm?.(false)
	resolveLeaveConfirm = null
}

function confirmLeaveWithoutSaving() {
	showLeaveConfirm.value = false
	resolveLeaveConfirm?.(true)
	resolveLeaveConfirm = null
}

async function confirmLeaveAndSave() {
	await savePersonalInfoChanges()
	showLeaveConfirm.value = false
	resolveLeaveConfirm?.(!preferencesError.value)
	resolveLeaveConfirm = null
}

function handleGuestLanguageChange(language: typeof languageDraft.value): void {
	languageDraft.value = language
	store.updateLanguage(language)
}

onMounted(async () => {
	if (!store.isUserLoggedIn) {
		syncLanguageFromProfile()
		return
	}

	try {
		await Promise.all([store.fetchBasicCurrentUserDetails(), loadPreferences()])
		syncPersonalInfoFromProfile()
		syncLanguageFromProfile()
		syncDefaultPageFromProfile()
		syncUnitFromProfile()
	} finally {
		isInitialProfileLoading.value = false
	}
})

onUnmounted(() => {
	if (savedBadgeTimeout) clearTimeout(savedBadgeTimeout)
})
</script>

<style scoped>
.settings-page {
	min-height: 100%;
	background: var(--neutral-50);
}

.settings-fade-enter-active,
.settings-fade-leave-active {
	transition: opacity 0.18s ease;
}

.settings-fade-enter-from,
.settings-fade-leave-to {
	opacity: 0;
}

.settings-shell {
	max-width: 720px;
	margin: 0 auto;
	padding: var(--spacing-lg);
	padding-bottom: calc(var(--spacing-lg) + var(--space-64));
}

.skeleton-block {
	display: block;
	border-radius: var(--border-radius-sm);
	background: var(--neutral-200);
}

.skeleton-title {
	width: 220px;
	height: var(--space-32);
}

.error-message {
	margin: 0 0 var(--spacing-md);
	color: var(--accent-warning);
	font-size: var(--font-size-small);
}

.settings-card {
	margin-bottom: var(--spacing-lg);

	.label {
		display: flex;
		align-items: center;
		gap: var(--spacing-sm);
		margin: 0 0 var(--spacing-md);
		color: var(--neutral-600);
		font-size: var(--font-size-xs);
		font-weight: var(--font-weight-semibold);
		text-transform: uppercase;
		letter-spacing: 0.04em;
	}

	.row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: var(--spacing-md);
		padding: var(--spacing-md);
		border: var(--border-width-thin) solid var(--neutral-200);
		border-bottom: none;
		background: var(--neutral-0);

		&:first-of-type {
			border-radius: var(--border-radius-lg) var(--border-radius-lg) 0 0;
		}

		&:last-of-type {
			border-bottom: var(--border-width-thin) solid var(--neutral-200);
			border-radius: 0 0 var(--border-radius-lg) var(--border-radius-lg);
		}

		&:only-of-type {
			border-radius: var(--border-radius-lg);
		}

		&.placeholder {
			opacity: 0.6;
			cursor: not-allowed;
		}

		&.expandable {
			flex-direction: column;
			align-items: stretch;
			gap: var(--spacing-md);
		}

		.row-header {
			display: flex;
			align-items: center;
			justify-content: space-between;
			gap: var(--spacing-md);

			.copy {
				flex: 1 1 0;
			}
		}

		.copy {
			min-width: 0;

			h3 {
				display: flex;
				align-items: center;
				gap: var(--spacing-sm);
				margin: 0;
				font-size: var(--font-size-regular);
				font-weight: var(--font-weight-regular);
				color: var(--neutral-900);
			}

			p {
				margin: var(--spacing-xxs) 0 0;
				color: var(--neutral-600);
				font-size: var(--font-size-small);
			}
		}

		.field {
			flex: 1 1 0;

			input {
				max-width: 320px;
			}
		}
	}

	.channel-checkboxes {
		display: flex;
		flex-wrap: wrap;
		flex-shrink: 0;
		gap: var(--spacing-xs);
		padding-top: var(--spacing-sm);
	}

	.identity-row {
		display: flex;
		align-items: center;
		gap: var(--spacing-md);
		margin-bottom: var(--spacing-md);
	}

	.avatar {
		display: flex;
		flex-shrink: 0;
		align-items: center;
		justify-content: center;
		width: var(--space-40);
		height: var(--space-40);
		border-radius: var(--border-radius-full);
		background: var(--primary-green);
		color: var(--neutral-0);
		font-size: var(--font-size-base);
		font-weight: var(--font-weight-semibold);
		line-height: 1;
	}

	.identity {
		min-width: 0;

		h3 {
			margin: 0;
			font-size: var(--font-size-regular);
			font-weight: var(--font-weight-medium);
			color: var(--neutral-900);
		}

		.email {
			margin: var(--spacing-xxs) 0 0;
			color: var(--neutral-600);
			font-size: var(--font-size-small);
			overflow-wrap: anywhere;
		}
	}

	.field-grid {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: var(--spacing-sm);
		padding: var(--spacing-md);
		border: var(--border-width-thin) solid var(--neutral-200);
		border-radius: var(--border-radius-lg);
		background: var(--neutral-0);
	}

	.field {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xxs);
		min-width: 0;

		h3 {
			display: flex;
			align-items: center;
			gap: var(--spacing-sm);
			margin: 0;
			font-size: var(--font-size-regular);
			font-weight: var(--font-weight-regular);
			color: var(--neutral-900);
			padding-bottom: var(--spacing-xxs);
		}

		input {
			width: 100%;
			height: var(--space-28);
			padding: 0 var(--spacing-sm);
			border: var(--border-width-thin) solid var(--neutral-200);
			border-radius: var(--border-radius-sm);
			background: var(--neutral-50);
			color: var(--neutral-900);
			font-size: var(--font-size-small);
			box-sizing: border-box;
		}
	}

	.field .warning-message {
		margin: 0;
	}

	.field .note {
		margin: 0;
		color: var(--neutral-600);
		font-size: var(--font-size-small);

		&.error {
			color: var(--accent-warning);
		}
	}

	.phone-popover {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-sm);
		width: 280px;
		padding: var(--spacing-md);
		border: var(--border-width-thin) solid var(--neutral-200);
		border-radius: var(--border-radius-md);
		background: var(--neutral-0);
		box-shadow: var(--shadow-lg);
		z-index: 100;

		input {
			width: 100%;
			height: var(--space-28);
			padding: 0 var(--spacing-sm);
			border: var(--border-width-thin) solid var(--neutral-200);
			border-radius: var(--border-radius-sm);
			background: var(--neutral-50);
			color: var(--neutral-900);
			font-size: var(--font-size-small);
			box-sizing: border-box;

			&:focus {
				border-color: var(--primary-green);
				outline: 2px solid color-mix(in srgb, var(--primary-green) 20%, transparent);
				outline-offset: 0;
			}
		}

		label {
			display: block;
			padding-bottom: var(--spacing-xs);
			color: var(--neutral-600);
			font-size: var(--font-size-xxs);
			font-weight: var(--font-weight-semibold);
			text-transform: uppercase;
			letter-spacing: 0.04em;
		}

		.feedback {
			margin: 0;
			min-height: 1lh;
			font-size: var(--font-size-small);

			&.success {
				color: var(--primary-green);
			}

			&.error {
				color: var(--accent-warning);
			}
		}

		.buttons {
			display: flex;
			justify-content: flex-end;
			gap: var(--spacing-xs);
		}
	}
}

.warning-message {
	margin-top: var(--spacing-xxs);
	color: var(--accent-version-amber-text);
	font-size: var(--font-size-small);
}

.status-pill {
	display: inline-flex;
	align-items: center;
	min-height: var(--space-20);
	padding: 0 var(--spacing-sm);
	border-radius: var(--border-radius-full);
	background: var(--neutral-100);
	color: var(--neutral-600);
	font-size: var(--font-size-xs);
	font-weight: var(--font-weight-medium);
	white-space: nowrap;

	&.dirty,
	&.warning {
		background: var(--accent-version-amber-bg);
		color: var(--accent-version-amber-text);
	}

	&.success,
	&.verified {
		background: var(--accent-highlight);
		color: var(--primary-green);
	}
}

.pill-toggle {
	display: inline-flex;
	flex-shrink: 0;
	gap: var(--spacing-xxs);
	padding: var(--spacing-xxs);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-md);
	background: var(--neutral-100);

	.option {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		min-height: var(--space-32);
		padding: 0 var(--spacing-md);
		border-radius: var(--border-radius-sm);
		color: var(--neutral-600);
		cursor: pointer;
		font-size: var(--font-size-small);
		font-weight: var(--font-weight-semibold);
		white-space: nowrap;

		input {
			position: absolute;
			opacity: 0;
			pointer-events: none;
		}

		&.selected {
			background: var(--neutral-0);
			color: var(--neutral-900);
			box-shadow: var(--shadow-sm);
		}
	}
}

.default-page-options {
	display: flex;
	flex-wrap: wrap;
	justify-content: flex-end;
	gap: var(--spacing-xs);
	max-width: 320px;
}

.save-bar-fade-enter-active,
.save-bar-fade-leave-active {
	transition: transform 0.2s ease;
}

.save-bar-fade-enter-from,
.save-bar-fade-leave-to {
	transform: translateY(100%);
}

.save-bar {
	position: fixed;
	left: 0;
	right: 0;
	bottom: 0;
	z-index: 50;
	display: flex;
	align-items: center;
	padding: 20px 0;
	border-top: var(--border-width-thin) solid var(--neutral-200);
	background: var(--neutral-0);
	box-shadow: var(--shadow-lg);
}

@media (max-width: 768px) {
	.save-bar {
		bottom: calc(var(--space-56) + env(safe-area-inset-bottom));
		z-index: 101;
		padding: var(--spacing-sm) 0;
	}
}

.save-bar-inner {
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: var(--spacing-sm);
	width: 100%;
	max-width: 720px;
	margin: 0 auto;
	padding: 0 var(--spacing-lg);
}

@media (max-width: 640px) {
	.settings-shell {
		padding: var(--spacing-md);
	}

	.channel-checkboxes :deep(.btn) {
		min-width: fit-content;
	}

	.settings-card .row {
		flex-direction: column;
		align-items: flex-start;

		.copy {
			h3 {
				font-size: var(--font-size-small);
			}

			p {
				font-size: var(--font-size-xs);
			}
		}

		&:has(> .pill-toggle),
		&:has(> .searches-controls),
		&:has(> .switch) {
			flex-direction: row;
			align-items: center;

			.copy {
				flex: 1 1 0;
				min-width: 0;
			}
		}

		.field {
			flex: none;
			align-self: stretch;
		}
	}

	.field-grid {
		grid-template-columns: 1fr;
	}

	.save-bar-inner {
		justify-content: space-between;

		button {
			flex: 1 1 0;
		}
	}

	.modal-actions {
		flex-direction: column;

		button {
			width: 100%;
		}
	}
}

.modal-overlay {
	position: fixed;
	inset: 0;
	z-index: 200;
	display: flex;
	align-items: center;
	justify-content: center;
	background: rgba(0, 0, 0, 0.5);
	backdrop-filter: blur(4px);
}

.modal-content {
	position: relative;
	display: flex;
	flex-direction: column;
	gap: var(--spacing-sm);
	width: 90%;
	max-width: 420px;
	padding: var(--spacing-lg);
	border-radius: var(--border-radius-lg);
	background: var(--neutral-0);

	h3 {
		margin: 0;
		padding-right: var(--spacing-xl);
		font-size: var(--font-size-base);
		font-weight: var(--font-weight-semibold);
		color: var(--neutral-900);
	}

	p {
		margin: 0;
		color: var(--neutral-600);
		font-size: var(--font-size-small);
	}

	.icon-button {
		position: absolute;
		top: var(--spacing-md);
		right: var(--spacing-md);
		width: var(--size-icon-xl);
		height: var(--size-icon-xl);
		flex-shrink: 0;
		border: none;
		background: transparent;
		color: var(--neutral-600);
		cursor: pointer;
		padding: var(--spacing-xxs);
		display: inline-flex;
		align-items: center;
		justify-content: center;

		&:hover {
			color: var(--neutral-900);
		}

		.close-icon {
			width: var(--size-icon-sm);
			height: var(--size-icon-sm);
			stroke: currentColor;
			fill: none;
			stroke-width: 2;
			stroke-linecap: round;
		}
	}
}

.modal-actions {
	display: flex;
	flex-wrap: wrap;
	justify-content: space-between;
	gap: var(--spacing-sm);
	margin-top: var(--spacing-sm);
}
</style>
